import {
  defineExperiment,
  type CustomExperimentSpec,
  type PanelRow,
} from '@learnforge/demo-render/harness';
import { safeStorage } from '@learnforge/demo-kit';
import type { SetsContext, SetsKey } from '../types';
import { powerSet, latticeEdges, subsetRank } from '../sets';
import {
  createHasseLattice,
  type HasseLatticeHandle,
  type HasseNode,
} from '../render/hasse-lattice';

// ── State ────────────────────────────────────────────────────────────────────

export interface State {
  n: number;
  highlightRank: number | null;
  cube: boolean;
  advanced: boolean;
}

// ── Constants ────────────────────────────────────────────────────────────────

// Element alphabet: first n letters of the alphabet.
const ALPHABET = ['a', 'b', 'c', 'd', 'e'];

const ADVANCED_STORAGE_KEY = 'sets.power-set.advanced';

// ── Pure helpers ─────────────────────────────────────────────────────────────

/**
 * Build HasseNode array from a power-set over the first `n` elements.
 * ∅ gets the label '∅'; non-empty subsets get '{a,b}' style labels.
 */
export function buildNodes(n: number): HasseNode[] {
  const els = ALPHABET.slice(0, n);
  const subsets = powerSet(els);
  return subsets.map((subset) => ({
    rank: subsetRank(subset, els),
    label: subset.length === 0 ? '∅' : `{${subset.join(',')}}`,
  }));
}

// ── Spec ─────────────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<State, SetsKey, SetsContext> {
  return {
    labelKey: 'experiment.power-set.label',
    demoId: 'sets',
    advancedKey: 'power-set',

    initialState(ctx): State {
      return {
        n: 3,
        highlightRank: null,
        cube: false,
        advanced: ctx.advanced,
      };
    },

    panel(state, ctx): PanelRow<SetsKey, State>[] {
      const advanced = ctx.advanced;
      const rows: PanelRow<SetsKey, State>[] = [
        {
          kind: 'slider',
          id: 'set-size',
          labelKey: 'param.set-size',
          min: 0,
          max: advanced ? 5 : 4,
          step: 1,
          value: state.n,
          onChange: (v) => ({ ...state, n: v, highlightRank: null }),
        },
        {
          kind: 'advanced-toggle',
          labelKey: 'btn.advanced',
        },
      ];

      if (advanced) {
        rows.splice(rows.length - 1, 0, {
          kind: 'toggle',
          labelKey: 'param.cube-layout',
          value: state.cube,
          onChange: (v) => ({ ...state, cube: v, highlightRank: null }),
        });
      }

      return rows;
    },

    createRender(ctx, host, controls) {
      // ── Container ──
      const container = document.createElement('div');
      container.className = 'power-set-container';
      host.appendChild(container);

      // ── SVG canvas ──
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'power-set-svg');
      svg.setAttribute('viewBox', '0 0 400 380');
      svg.setAttribute('width', '400');
      svg.setAttribute('height', '380');
      container.appendChild(svg);

      // ── Readout ──
      const readout = document.createElement('div');
      readout.className = 'power-set-readout';
      container.appendChild(readout);

      // ── Lattice handle ──
      let latticeHandle: HasseLatticeHandle | null = null;
      let lastN = -1;
      let lastCube = false;

      function update(state: State): void {
        const { n, highlightRank, cube } = state;

        const els = ALPHABET.slice(0, n);
        const nodes = buildNodes(n);
        const edges = latticeEdges(n);

        // Rebuild lattice when n or cube layout changes.
        if (latticeHandle === null || n !== lastN || cube !== lastCube) {
          latticeHandle?.destroy();
          latticeHandle = createHasseLattice(svg, {
            nodes,
            edges,
            highlightRank: highlightRank ?? null,
            cube,
            onSelectRank(rank) {
              controls.setState((prev) => ({
                ...prev,
                highlightRank: prev.highlightRank === rank ? null : rank,
              }));
            },
          });
          lastN = n;
          lastCube = cube;
        } else {
          // Only update highlight.
          latticeHandle.update({ highlightRank: highlightRank ?? null });
        }

        // ── Readout: |𝒫(S)| = 2ⁿ ──
        const superscripts = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];
        const sup = String(n)
          .split('')
          .map((d) => superscripts[Number(d)] ?? d)
          .join('');
        const setLabel = els.length > 0 ? `S = {${els.join(',')}}` : 'S = ∅';
        readout.textContent = `${setLabel}   |𝒫(S)| = 2${sup} = ${1 << n}`;

        // Update advanced flag from storage on each render so it stays in sync.
        safeStorage.set(ADVANCED_STORAGE_KEY, ctx.advanced ? 'true' : 'false');
      }

      return {
        update,
        dispose() {
          latticeHandle?.destroy();
          latticeHandle = null;
        },
      };
    },

    applyShotState(state): State {
      // Shot pose: n=3, highlight the {a,b} subset (rank = 0b011 = 3).
      const els = ALPHABET.slice(0, 3);
      const abRank = subsetRank(['a', 'b'], els);
      return {
        ...state,
        n: 3,
        highlightRank: abRank,
        cube: false,
        advanced: false,
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
